import { QueryKeys } from "@/constants/Query";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import type { GenderIdentity } from "@curai/js-fhir-tools";
import { useQueryClient } from "@tanstack/react-query";

export const useUpdatePreferredName = (patientId?: string) => {
  const queryClient = useQueryClient();

  return useApiMutation<void, { preferredName: string | null }>({
    mutationOptions: {
      mutationKey:
        QueryKeys.Patient.MedicalRecords.Profile.PREFERRED_NAME(patientId),
      mutationFn: async ({ preferredName }) => {
        await axiosInstance.patch(
          `/clinic/patients/patient/${patientId}/preferred-name`,
          { preferredName },
        );
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.DEFAULT(patientId),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Profile.DEFAULT(patientId),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Demographics.DEFAULT(patientId),
        });
      },
    },
  });
};

export const useUpdateGenderIdentity = (patientId?: string) => {
  const queryClient = useQueryClient();

  return useApiMutation<void, { genderIdentity: GenderIdentity | null }>({
    mutationOptions: {
      mutationKey:
        QueryKeys.Patient.MedicalRecords.Profile.GENDER_IDENTITY(patientId),
      mutationFn: async ({ genderIdentity }) => {
        await axiosInstance.patch(
          `/clinic/patients/patient/${patientId}/gender-identity`,
          { genderIdentity },
        );
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.DEFAULT(patientId),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Profile.DEFAULT(patientId),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Demographics.DEFAULT(patientId),
        });
      },
    },
  });
};

export const useUpdatePronouns = (patientId?: string) => {
  const queryClient = useQueryClient();

  return useApiMutation<void, { pronouns: string | null }>({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.MedicalRecords.Profile.PRONOUNS(patientId),
      mutationFn: async ({ pronouns }) => {
        await axiosInstance.patch(
          `/clinic/patients/patient/${patientId}/pronouns`,
          { pronouns },
        );
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.DEFAULT(patientId),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Profile.DEFAULT(patientId),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Demographics.DEFAULT(patientId),
        });
      },
    },
  });
};
